import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import { Icon } from '@iconify/react/dist/iconify.js';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../services/api';
import ViewResaleUnitModal from './ViewResaleUnitModal';

const formatMoney = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const number = Number(value);
    if (Number.isNaN(number)) return '';
    return number.toLocaleString('en-US');
};

const getStatusLabel = (status) => {
    if (status === 'approved') return 'Approved';
    if (status === 'rejected') return 'Rejected';
    return 'Pending';
};

const getStatusClass = (status) => {
    if (status === 'approved') return 'success';
    if (status === 'rejected') return 'danger';
    return 'warning';
};

const ResaleUnitsTable = () => {
    const [resaleUnits, setResaleUnits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterText, setFilterText] = useState('');
    // Show every listing by default; the admin narrows down to pending when needed.
    const [selectedStatus, setSelectedStatus] = useState('');
    const [currentUnit, setCurrentUnit] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [processing, setProcessing] = useState(false);
    const [settings, setSettings] = useState(null);

    // The commission percentages are read from the settings, never hardcoded.
    const fetchSettings = async () => {
        try {
            const res = await api.get('/resaleSettings');
            setSettings(res.data.response || null);
        } catch (error) {
            console.error('Error fetching resale settings:', error);
        }
    };

    const fetchResaleUnits = async () => {
        setLoading(true);
        try {
            const res = await api.get('/resaleUnits/unpaginated');
            setResaleUnits(res.data.response?.data || []);
        } catch (error) {
            console.error('Error fetching resale units:', error);
            toast.error(error?.response?.data?.message || 'Failed to load resale units.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchResaleUnits();
        fetchSettings();
    }, []);

    const handleApprove = async (row) => {
        if (!row?.id) return;
        setProcessing(true);
        try {
            await api.post(`/resaleUnits/${row.id}/approve`);
            toast.success('Resale unit approved successfully!');
            fetchResaleUnits();
        } catch (error) {
            console.error('Approval error:', error);
            toast.error(error?.response?.data?.message || 'Failed to approve the resale unit.');
        } finally {
            setProcessing(false);
        }
    };

    const handleReject = async () => {
        if (!currentUnit?.id) {
            toast.warn('No resale unit selected.');
            return;
        }
        if (!rejectionReason.trim()) {
            toast.warn('Please enter a rejection reason.');
            return;
        }
        setProcessing(true);
        try {
            await api.post(`/resaleUnits/${currentUnit.id}/reject`, { reason: rejectionReason });
            toast.success('Resale unit rejected successfully!');
            setShowRejectModal(false);
            setRejectionReason('');
            setCurrentUnit(null);
            fetchResaleUnits();
        } catch (error) {
            console.error('Rejection error:', error);
            toast.error(error?.response?.data?.message || 'Failed to reject the resale unit.');
        } finally {
            setProcessing(false);
        }
    };

    const columns = useMemo(
        () => [
            {
                name: 'ID',
                selector: (row) => row?.id ?? 'N/A',
                sortable: true,
                width: '80px',
            },
            {
                name: 'Seller',
                selector: (row) => row?.sellerName || '',
                sortable: true,
                minWidth: '140px',
            },
            {
                name: 'Seller Number',
                selector: (row) => row?.sellerMobile || '',
                sortable: true,
                minWidth: '140px',
            },
            {
                name: 'Developer',
                selector: (row) => row?.developerName || '',
                sortable: true,
                minWidth: '140px',
            },
            {
                name: 'Project',
                selector: (row) => row?.projectName || '',
                sortable: true,
                minWidth: '140px',
            },
            {
                name: 'Type',
                selector: (row) => row?.type || '',
                sortable: true,
                minWidth: '110px',
            },
            {
                name: 'Contract Price',
                selector: (row) => Number(row?.contractPrice ?? 0),
                format: (row) => formatMoney(row?.contractPrice),
                sortable: true,
                minWidth: '130px',
            },
            {
                name: 'Cash To Seller',
                selector: (row) => Number(row?.paidAmount ?? 0),
                format: (row) => formatMoney(row?.paidAmount),
                sortable: true,
                minWidth: '130px',
            },
            {
                name:
                    settings?.buyer_commission_percent !== undefined &&
                    settings?.buyer_commission_percent !== null
                        ? `Our Commission (${settings.buyer_commission_percent}%)`
                        : 'Our Commission',
                selector: (row) => Number(row?.buyerCommissionAmount ?? 0),
                format: (row) => formatMoney(row?.buyerCommissionAmount),
                sortable: true,
                minWidth: '140px',
            },
            {
                name: 'Docs',
                cell: (row) =>
                    (row?.documents || []).length > 0 ? (
                        <span>{row.documents.length} file(s)</span>
                    ) : (
                        <span className="text-danger">Missing</span>
                    ),
                sortable: false,
                minWidth: '100px',
            },
            {
                name: 'Interests',
                selector: (row) => row?.interests_count ?? 0,
                sortable: true,
                width: '110px',
            },
            {
                name: 'Status',
                cell: (row) => (
                    <span
                        className={`bg-${getStatusClass(row?.status)}-focus text-${getStatusClass(
                            row?.status
                        )}-main px-24 py-4 rounded-pill fw-medium text-sm`}
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        {getStatusLabel(row?.status)}
                    </span>
                ),
                sortable: true,
                minWidth: '140px',
            },
            {
                name: 'Actions',
                cell: (row) => (
                    <div className="d-flex">
                        <button
                            onClick={() => {
                                setCurrentUnit(row);
                                setShowViewModal(true);
                            }}
                            title="View"
                            className="w-32-px h-32-px me-8 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                        >
                            <Icon icon="iconamoon:eye-light" />
                        </button>
                        {row?.status !== 'approved' && (
                            <button
                                onClick={() => handleApprove(row)}
                                title="Approve"
                                disabled={processing}
                                className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                            >
                                <Icon icon="mdi:check-circle-outline" />
                            </button>
                        )}
                        {row?.status !== 'rejected' && (
                            <button
                                onClick={() => {
                                    setCurrentUnit(row);
                                    setShowRejectModal(true);
                                }}
                                title="Reject"
                                className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                            >
                                <Icon icon="mdi:close-circle-outline" />
                            </button>
                        )}
                    </div>
                ),
                ignoreRowClick: true,
                allowOverflow: true,
                button: true,
                minWidth: '150px',
                grow: 0,
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [processing, settings]
    );

    const filteredData = useMemo(
        () =>
            resaleUnits.filter((item) => {
                const search = filterText.toLowerCase();
                const matchesSearch =
                    !filterText ||
                    String(item?.id ?? '').includes(search) ||
                    (item?.sellerName || '').toLowerCase().includes(search) ||
                    (item?.sellerMobile || '').toLowerCase().includes(search) ||
                    (item?.developerName || '').toLowerCase().includes(search) ||
                    (item?.projectName || '').toLowerCase().includes(search) ||
                    (item?.type || '').toLowerCase().includes(search) ||
                    (item?.location || '').toLowerCase().includes(search) ||
                    (item?.unitCode || '').toLowerCase().includes(search);

                const matchesStatus = selectedStatus ? item?.status === selectedStatus : true;

                return matchesSearch && matchesStatus;
            }),
        [resaleUnits, filterText, selectedStatus]
    );

    const csvHeaders = [
        { label: 'ID', key: 'id' },
        { label: 'Seller', key: 'sellerName' },
        { label: 'Seller Number', key: 'sellerMobile' },
        { label: 'Seller Email', key: 'sellerEmail' },
        { label: 'Developer', key: 'developerName' },
        { label: 'Project', key: 'projectName' },
        { label: 'Type', key: 'type' },
        { label: 'Location', key: 'location' },
        { label: 'Area', key: 'area' },
        { label: 'Bedrooms', key: 'numberOfBedrooms' },
        { label: 'Bathrooms', key: 'numberOfBathrooms' },
        { label: 'Finishing', key: 'finishing' },
        { label: 'Contract Price', key: 'contractPrice' },
        { label: 'Cash To Seller', key: 'paidAmount' },
        { label: 'Remaining To Developer', key: 'remainingAmount' },
        { label: 'Installment', key: 'installmentAmount' },
        { label: 'Installment Frequency', key: 'installmentFrequency' },
        { label: 'Developer Price Today', key: 'marketPriceToday' },
        { label: 'Buyer Commission %', key: 'buyerCommissionPercent' },
        { label: 'Buyer Commission Amount', key: 'buyerCommissionAmount' },
        { label: 'Buyer Total Cash Needed', key: 'buyerTotalCashNeeded' },
        { label: 'Seller Commission %', key: 'sellerCommissionPercent' },
        { label: 'Seller Commission Amount', key: 'sellerCommissionAmount' },
        { label: 'Seller Net Payout', key: 'sellerNetPayout' },
        { label: 'Delivery Date', key: 'deliveryDate' },
        { label: 'Status', key: 'status' },
        { label: 'Created At', key: 'created_at' },
    ];

    const customStyles = {
        rows: {
            style: {
                fontSize: '15px',
            },
        },
    };

    const pendingCount = resaleUnits.filter((item) => item?.status === 'pending').length;

    return (
        <div className="card basic-data-table px-3 py-3">
            <div className="card-body">
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                        <input
                            type="text"
                            placeholder="Search resale units..."
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                            className="form-control"
                            style={{ width: '220px' }}
                        />
                        <select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            className="form-select"
                            style={{ width: '200px' }}
                            aria-label="Select status"
                        >
                            <option value="pending">Pending review ({pendingCount})</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                            <option value="">All statuses</option>
                        </select>
                        <button className="btn btn-outline-secondary" onClick={fetchResaleUnits} disabled={loading}>
                            Refresh
                        </button>
                    </div>
                    <CSVLink
                        data={filteredData}
                        headers={csvHeaders}
                        filename={`resale-units-${selectedStatus || 'all'}.csv`}
                        className="btn btn-secondary"
                        target="_blank"
                    >
                        Export CSV
                    </CSVLink>
                </div>

                {settings && (
                    <div className="text-sm text-secondary-light mb-3">
                        Commission applied to every listing below: buyer {settings.buyer_commission_percent}% ·
                        seller {settings.seller_commission_percent}% of the contract value. Change them from{' '}
                        <Link to="/settings">Settings</Link>.
                    </div>
                )}

                <div className="table-responsive">
                    <DataTable
                        columns={columns}
                        data={filteredData}
                        progressPending={loading}
                        pagination
                        highlightOnHover
                        responsive
                        striped
                        customStyles={customStyles}
                        noDataComponent={
                            loading ? '' : <div className="p-4 text-center">No resale units found.</div>
                        }
                        paginationComponentOptions={{
                            rowsPerPageText: 'Rows per page:',
                            rangeSeparatorText: 'of',
                            selectAllRowsItem: true,
                            selectAllRowsItemText: 'All',
                        }}
                    />
                </div>
            </div>

            {showViewModal && currentUnit && (
                <ViewResaleUnitModal
                    show={showViewModal}
                    handleClose={() => {
                        setShowViewModal(false);
                        setCurrentUnit(null);
                    }}
                    resaleUnit={currentUnit}
                />
            )}

            <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Reject Resale Unit #{currentUnit?.id}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Please provide a reason for rejecting this listing (the seller receives it):</p>
                    <label htmlFor="resaleRejectionReason" className="form-label visually-hidden">
                        Rejection Reason
                    </label>
                    <textarea
                        id="resaleRejectionReason"
                        className="form-control"
                        placeholder="Enter rejection reason (required)"
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        rows={3}
                        required
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setShowRejectModal(false);
                            setRejectionReason('');
                        }}
                    >
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleReject} disabled={!rejectionReason.trim() || processing}>
                        Confirm Rejection
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ResaleUnitsTable;
